import { useState, useEffect, useCallback } from "react";
import { Plus, Clock, RefreshCw, AlertTriangle, CheckCircle2, Pencil, History, Unlock, LogIn, CalendarCheck, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDate, formatDateTime } from "../lib/date";

const eventTypeLabels = {
  check_in: "Entrada",
  meal_out: "Salida comida",
  meal_in: "Regreso comida",
  check_out: "Salida",
};

const statusMap = {
  present: { label: "Presente", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  absent: { label: "Ausente", cls: "bg-red-50 text-red-700 border-red-200" },
  incomplete: { label: "Incompleto", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  vacation: { label: "Vacaciones", cls: "bg-blue-50 text-blue-700 border-blue-200" },
};

export default function Attendance() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [filterBranch, setFilterBranch] = useState("all");
  const [onlyNoCheckout, setOnlyNoCheckout] = useState(false);
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [events, setEvents] = useState([]);
  const [records, setRecords] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [alertFilterDate, setAlertFilterDate] = useState("all"); // "all" | YYYY-MM-DD
  const [alertFilterEmployee, setAlertFilterEmployee] = useState("all"); // "all" | employee_id
  const [alertFilterBranch, setAlertFilterBranch] = useState("all"); // "all" | branch_id
  const [editRecord, setEditRecord] = useState(null); // record being edited
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [auditRecord, setAuditRecord] = useState(null); // record whose history is shown
  const [auditHistory, setAuditHistory] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [reopenRecord, setReopenRecord] = useState(null);
  const [reopenReason, setReopenReason] = useState("");
  const [reopening, setReopening] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [deletingRecord, setDeletingRecord] = useState(false);
  const [consolidating, setConsolidating] = useState(false);
  const [mealCreditRecord, setMealCreditRecord] = useState(null);
  const [mealCreditReason, setMealCreditReason] = useState("");
  const [mealCrediting, setMealCrediting] = useState(false);
  const [confirmResolveAll, setConfirmResolveAll] = useState(false);
  const [manualEmp, setManualEmp] = useState(null); // employee for manual record
  const [manualForm, setManualForm] = useState({});
  const [savingManual, setSavingManual] = useState(false);
  const [form, setForm] = useState({
    employee_id: "",
    branch_id: "",
    event_type: "check_in",
    event_time: "",
  });

  const fetchAlerts = useCallback(async () => {
    try {
      const params = { status: "pending" };
      if (alertFilterBranch !== "all") params.branch_id = alertFilterBranch;
      if (alertFilterDate !== "all") params.date = alertFilterDate;
      if (alertFilterEmployee !== "all") params.employee_id = alertFilterEmployee;
      const { data } = await api.get("/hr-alerts", { params });
      setAlerts(data);
    } catch {
      // silent
    }
  }, [alertFilterBranch, alertFilterDate, alertFilterEmployee]);

  const fetchData = useCallback(async () => {
    try {
      const params = { date };
      if (filterBranch !== "all") params.branch_id = filterBranch;
      const [evtRes, recRes, brRes, empRes] = await Promise.all([
        api.get("/attendance/events", { params }),
        api.get("/attendance/records", { params }),
        api.get("/branches"),
        api.get("/employees", { params: { status: "active", limit: 2000 } }),
      ]);
      setEvents(evtRes.data);
      setRecords(recRes.data);
      setBranches(brRes.data);
      setEmployees(empRes.data.items || []);
    } catch {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [date, filterBranch]);

  const resolveAlert = async (alertId) => {
    try {
      await api.post(`/hr-alerts/${alertId}/resolve`, {
        resolution_notes: "Revisado desde asistencia",
      });
      toast.success("Alerta resuelta");
      fetchAlerts();
    } catch {
      toast.error("No se pudo resolver la alerta");
    }
  };

  const resolveAllAlerts = async () => {
    try {
      const params = {};
      if (alertFilterBranch !== "all") params.branch_id = alertFilterBranch;
      if (alertFilterDate !== "all") params.date = alertFilterDate;
      if (alertFilterEmployee !== "all") params.employee_id = alertFilterEmployee;
      const { data } = await api.post("/hr-alerts/resolve-all", null, { params });
      toast.success(`${data.resolved} alerta(s) resueltas`);
      setConfirmResolveAll(false);
      fetchAlerts();
    } catch {
      toast.error("No se pudieron resolver las alertas");
    }
  };

  const RESOLVE_ALL_THRESHOLD = 20;
  const handleResolveAllClick = () => {
    if (alerts.length > RESOLVE_ALL_THRESHOLD) {
      setConfirmResolveAll(true);
    } else {
      resolveAllAlerts();
    }
  };

  // Parse an ISO datetime to a "HH:mm" string for <input type="time">, or "" if empty
  const isoToTime = (iso) => {
    if (!iso) return "";
    try {
      return iso.slice(11, 16);
    } catch {
      return "";
    }
  };
  // Given an existing ISO string (to keep date + timezone) and a new "HH:mm", return new ISO.
  const timeToIso = (originalIso, timeStr, recordDate) => {
    if (!timeStr) return null;
    const datePart = originalIso ? originalIso.slice(0, 10) : recordDate;
    const tzPart = originalIso && originalIso.length > 19 ? originalIso.slice(19) : "+00:00";
    return `${datePart}T${timeStr}:00${tzPart}`;
  };

  const openEditDialog = (rec) => {
    setEditRecord(rec);
    setEditForm({
      first_check_in_time: isoToTime(rec.first_check_in),
      final_check_out_time: isoToTime(rec.final_check_out),
      worked_hours: rec.worked_hours ?? 0,
      ordinary_hours: rec.ordinary_hours ?? 0,
      overtime_hours: rec.overtime_hours ?? 0,
      lateness_minutes: rec.lateness_minutes ?? 0,
      attendance_status: rec.attendance_status || "present",
      notes: rec.notes || "",
      recalc_hours: false,
    });
  };
  const closeEditDialog = () => {
    setEditRecord(null);
    setEditForm({});
  };

  const openManualDialog = (emp) => {
    setManualEmp(emp);
    setManualForm({
      first_check_in_time: "",
      final_check_out_time: "",
      attendance_status: "present",
      notes: "",
    });
  };
  const closeManualDialog = () => {
    setManualEmp(null);
    setManualForm({});
  };
  const saveManual = async () => {
    if (!manualEmp) return;
    setSavingManual(true);
    try {
      const payload = {
        employee_id: manualEmp.id,
        date,
        branch_id: manualEmp.primary_branch_id || (filterBranch !== "all" ? filterBranch : null),
        attendance_status: manualForm.attendance_status,
        notes: manualForm.notes || "",
      };
      if (manualForm.first_check_in_time)
        payload.first_check_in = `${date}T${manualForm.first_check_in_time}:00`;
      if (manualForm.final_check_out_time)
        payload.final_check_out = `${date}T${manualForm.final_check_out_time}:00`;
      await api.post("/attendance/records/manual", payload);
      toast.success("Registro creado");
      closeManualDialog();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear el registro");
    } finally {
      setSavingManual(false);
    }
  };

  const openAuditDialog = async (rec) => {
    setAuditRecord(rec);
    setAuditLoading(true);
    try {
      const { data } = await api.get(`/attendance/records/${rec.id}/audit`);
      setAuditHistory(data);
    } catch {
      setAuditHistory([]);
    } finally {
      setAuditLoading(false);
    }
  };
  const closeAuditDialog = () => {
    setAuditRecord(null);
    setAuditHistory([]);
  };

  const [reentryRecord, setReentryRecord] = useState(null);
  const [reentryReason, setReentryReason] = useState("");
  const [allowingReentry, setAllowingReentry] = useState(false);

  const openReopenDialog = (rec) => {
    setReopenRecord(rec);
    setReopenReason("");
  };
  const closeReopenDialog = () => {
    setReopenRecord(null);
    setReopenReason("");
  };
  const confirmReopen = async () => {
    if (!reopenRecord) return;
    setReopening(true);
    try {
      const { data } = await api.post(
        `/attendance/records/${reopenRecord.id}/reopen`,
        { reason: reopenReason }
      );
      toast.success(data.detail || "Salida anulada");
      closeReopenDialog();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo anular la salida");
    } finally {
      setReopening(false);
    }
  };

  const openMealCreditDialog = (rec) => {
    setMealCreditRecord(rec);
    setMealCreditReason("No tomó los tiempos de comida y salió antes");
  };
  const closeMealCreditDialog = () => {
    setMealCreditRecord(null);
    setMealCreditReason("");
  };
  const confirmMealCredit = async () => {
    if (!mealCreditRecord) return;
    if (!mealCreditReason.trim()) {
      toast.error("Indique el motivo");
      return;
    }
    setMealCrediting(true);
    try {
      await api.post(
        `/attendance/records/${mealCreditRecord.id}/credit-meal-time`,
        { reason: mealCreditReason.trim() }
      );
      toast.success("Día registrado como laborado completo");
      closeMealCreditDialog();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo aplicar el crédito");
    } finally {
      setMealCrediting(false);
    }
  };
  const revertMealCredit = async (rec) => {
    try {
      await api.post(`/attendance/records/${rec.id}/revert-meal-credit`);
      toast.success("Crédito revertido");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo revertir");
    }
  };

  const openReentryDialog = (rec) => {
    setReentryRecord(rec);
    setReentryReason("");
  };
  const closeReentryDialog = () => {
    setReentryRecord(null);
    setReentryReason("");
  };
  const confirmReentry = async () => {
    if (!reentryRecord) return;
    setAllowingReentry(true);
    try {
      const { data } = await api.post(
        `/attendance/records/${reentryRecord.id}/allow-reentry`,
        { reason: reentryReason }
      );
      toast.success(data.detail || "Reingreso permitido");
      closeReentryDialog();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo permitir el reingreso");
    } finally {
      setAllowingReentry(false);
    }
  };

  const saveEdit = async () => {
    if (!editRecord) return;
    setSavingEdit(true);
    try {
      const payload = {
        attendance_status: editForm.attendance_status,
        notes: editForm.notes,
        lateness_minutes: parseFloat(editForm.lateness_minutes) || 0,
      };
      const newCi = timeToIso(editRecord.first_check_in, editForm.first_check_in_time, editRecord.date);
      const newCo = timeToIso(editRecord.final_check_out, editForm.final_check_out_time, editRecord.date);
      if (newCi) payload.first_check_in = newCi;
      if (newCo) payload.final_check_out = newCo;
      if (!editForm.recalc_hours) {
        // Respect manual hour values
        payload.worked_hours = parseFloat(editForm.worked_hours) || 0;
        payload.ordinary_hours = parseFloat(editForm.ordinary_hours) || 0;
        payload.overtime_hours = parseFloat(editForm.overtime_hours) || 0;
      }
      await api.patch(`/attendance/records/${editRecord.id}`, payload);
      toast.success("Registro actualizado");
      closeEditDialog();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteRecord) return;
    setDeletingRecord(true);
    try {
      const { data } = await api.delete(`/attendance/records/${deleteRecord.id}`);
      toast.success(`Registro eliminado (${data.events_deleted || 0} marca(s) borradas)`);
      setDeleteRecord(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar el registro");
    } finally {
      setDeletingRecord(false);
    }
  };


  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const getEmployeeName = (id) =>
    employees.find((e) => e.id === id)?.full_name || id;
  const getEmployeeCode = (id) =>
    employees.find((e) => e.id === id)?.employee_code || "";
  const isCrossBranchEvent = (evt) => {
    const emp = employees.find((e) => e.id === evt.employee_id);
    return emp?.primary_branch_id && evt.branch_id && emp.primary_branch_id !== evt.branch_id;
  };
  const getBranchName = (id) =>
    branches.find((b) => b.id === id)?.name || "\u2014";

  const handleCreateEvent = async () => {
    if (!form.employee_id || !form.branch_id || !form.event_time) {
      toast.error("Empleado, sucursal y hora son requeridos");
      return;
    }
    const fullTime = `${date}T${form.event_time}:00`;
    try {
      await api.post("/attendance/events", {
        employee_id: form.employee_id,
        branch_id: form.branch_id,
        event_type: form.event_type,
        event_time: fullTime,
        source: "manual_adjustment_hr",
      });
      toast.success("Marca registrada");
      setDialogOpen(false);
      setForm({ employee_id: "", branch_id: "", event_type: "check_in", event_time: "" });
      fetchData();
    } catch {
      toast.error("Error al registrar marca");
    }
  };

  const handleGenerate = async () => {
    setConsolidating(true);
    try {
      const params = { date };
      if (filterBranch !== "all") params.branch_id = filterBranch;
      const { data } = await api.post("/attendance/records/generate", null, { params });
      toast.success(`${data.generated} registros generados`);
      fetchData();
    } catch {
      toast.error("Error al generar registros");
    } finally {
      setConsolidating(false);
    }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return "\u2014";
    try {
      return isoStr.substring(11, 16);
    } catch {
      return isoStr;
    }
  };

  const visibleRecords = onlyNoCheckout
    ? records.filter((r) => r.first_check_in && !r.final_check_out)
    : records;

  return (
    <div data-testid="attendance-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
            Asistencia
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Monitor diario de asistencia biometrica
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleGenerate}
            disabled={consolidating}
            data-testid="generate-records-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${consolidating ? "animate-spin" : ""}`} />
            {consolidating ? "Consolidando…" : "Consolidar"}
          </Button>
          <Button
            onClick={() => {
              setForm({
                employee_id: "",
                branch_id: "",
                event_type: "check_in",
                event_time: "",
              });
              setDialogOpen(true);
            }}
            className="bg-zinc-900 text-white hover:bg-zinc-800"
            data-testid="add-attendance-event-button"
          >
            <Plus className="w-4 h-4 mr-2" /> Nueva marca
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-[180px]"
          data-testid="attendance-date-input"
        />
        <Select value={filterBranch} onValueChange={setFilterBranch}>
          <SelectTrigger className="w-[200px]" data-testid="attendance-filter-branch">
            <SelectValue placeholder="Sucursal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las sucursales</SelectItem>
            {branches.map((b) => (
              <SelectItem key={b.id} value={b.id}>
                {b.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant={onlyNoCheckout ? "default" : "outline"}
          onClick={() => setOnlyNoCheckout((v) => !v)}
          className={onlyNoCheckout ? "bg-red-600 text-white hover:bg-red-700" : "text-zinc-700"}
          data-testid="attendance-filter-no-checkout"
          title="Muestra solo los registros con entrada pero sin marca de salida"
        >
          <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
          {onlyNoCheckout ? "Mostrando sin salida" : "Sin marca de salida"}
        </Button>
      </div>

      {/* HR Alerts panel - always visible when there are pending alerts or filters applied */}
      {(alerts.length > 0 || alertFilterDate !== "all" || alertFilterEmployee !== "all" || alertFilterBranch !== "all") && (
        <Card className="bg-amber-50/40 border-amber-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)] mb-6" data-testid="hr-alerts-panel">
          <CardContent className="p-0">
            <div className="px-5 py-3 border-b border-amber-200/60 flex items-center gap-2 flex-wrap">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-900 mr-2">
                Alertas para RRHH · {alerts.length}
              </h3>
              <div className="flex-1" />
              {/* Branch filter */}
              <Select value={alertFilterBranch} onValueChange={setAlertFilterBranch}>
                <SelectTrigger
                  className="h-8 w-[160px] bg-white border-amber-300 text-amber-900 text-xs"
                  data-testid="alert-filter-branch"
                >
                  <SelectValue placeholder="Sucursal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las sucursales</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Date filter */}
              <Select value={alertFilterDate} onValueChange={setAlertFilterDate}>
                <SelectTrigger
                  className="h-8 w-[160px] bg-white border-amber-300 text-amber-900 text-xs"
                  data-testid="alert-filter-date"
                >
                  <SelectValue placeholder="Fecha" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las fechas</SelectItem>
                  <SelectItem value={date}>Dia seleccionado ({formatDate(date)})</SelectItem>
                  <SelectItem value={new Date().toISOString().slice(0, 10)}>
                    Hoy
                  </SelectItem>
                </SelectContent>
              </Select>
              {/* Employee filter */}
              <Select value={alertFilterEmployee} onValueChange={setAlertFilterEmployee}>
                <SelectTrigger
                  className="h-8 w-[200px] bg-white border-amber-300 text-amber-900 text-xs"
                  data-testid="alert-filter-employee"
                >
                  <SelectValue placeholder="Empleado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los empleados</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.full_name} · {e.employee_code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(alertFilterDate !== "all" || alertFilterEmployee !== "all" || alertFilterBranch !== "all") && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 text-xs text-amber-900 hover:bg-amber-100"
                  onClick={() => {
                    setAlertFilterDate("all");
                    setAlertFilterEmployee("all");
                    setAlertFilterBranch("all");
                  }}
                  data-testid="alert-filters-clear"
                >
                  Limpiar
                </Button>
              )}
              {alerts.length > 0 && (
                <Button
                  size="sm"
                  className="h-8 text-xs bg-amber-600 text-white hover:bg-amber-700"
                  onClick={handleResolveAllClick}
                  data-testid="alerts-resolve-all"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                  Resolver todas
                </Button>
              )}
            </div>
            {alerts.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <p className="text-xs text-amber-800/70">
                  No hay alertas que coincidan con los filtros seleccionados.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-amber-200/50">
                {alerts.map((a) => (
                  <div
                    key={a.id}
                    className="px-5 py-3 flex items-start gap-4 hover:bg-amber-100/30 transition-colors"
                    data-testid={`hr-alert-${a.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-medium text-amber-950">{a.title}</p>
                        <span className="text-[11px] font-mono text-amber-700">
                          {formatDate(a.date)}
                        </span>
                      </div>
                      <p className="text-xs text-amber-800/80 leading-snug">
                        <span className="font-medium">{a.employee_name}</span>
                        {a.employee_code ? ` · ${a.employee_code}` : ""} — {a.description}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100 h-8 text-xs shrink-0"
                      onClick={() => resolveAlert(a.id)}
                      data-testid={`hr-alert-resolve-${a.id}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                      Marcar resuelta
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Records Table */}
      <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)] mb-8">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-zinc-100">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-900">
              Registros consolidados ({visibleRecords.length})
              {onlyNoCheckout && <span className="ml-2 text-red-500 normal-case tracking-normal font-normal">· filtrado: sin marca de salida</span>}
            </h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-100 hover:bg-transparent">
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Fecha
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Empleado
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden sm:table-cell">
                  Sucursal
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Entrada
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden sm:table-cell">
                  Comidas
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Salida
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right hidden md:table-cell">
                  Hrs Ord.
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right hidden md:table-cell">
                  Hrs Extra
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">
                  Total Hrs
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Estado
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleRecords.map((r) => (
                <TableRow
                  key={r.id}
                  className="border-zinc-100"
                  data-testid={`attendance-record-${r.id}`}
                >
                  <TableCell className="text-sm text-zinc-600 font-mono whitespace-nowrap">
                    {formatDate(r.date)}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-sm text-zinc-900 font-medium">
                        {getEmployeeName(r.employee_id)}
                      </p>
                      <p className="text-xs text-zinc-400 font-mono">
                        {getEmployeeCode(r.employee_id)}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden sm:table-cell">
                    {getBranchName(r.branch_id)}
                    {(() => {
                      const emp = employees.find((e) => e.id === r.employee_id);
                      const cross = emp?.primary_branch_id && r.branch_id && emp.primary_branch_id !== r.branch_id;
                      return cross ? (
                        <Badge
                          variant="outline"
                          className="ml-2 bg-amber-50 text-amber-700 border-amber-200 text-[10px] font-medium"
                          title="Consolidado en una sucursal distinta a la asignada"
                          data-testid={`record-cross-branch-badge-${r.id}`}
                        >
                          Otra sucursal
                        </Badge>
                      ) : null;
                    })()}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-600 font-mono">
                    {formatTime(r.first_check_in)}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-600 hidden sm:table-cell">
                    {r.meal_count > 0 ? (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50 transition text-xs font-mono"
                            data-testid={`meal-breaks-${r.id}`}
                          >
                            <span className="font-semibold text-zinc-900">
                              {r.meal_count}×
                            </span>
                            <span className="text-zinc-500">
                              {Math.round(r.meal_total_minutes)} min
                            </span>
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-0" align="start">
                          <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between">
                            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-900">
                              Detalle comidas
                            </p>
                            <span className="text-xs font-mono text-zinc-500">
                              {Math.round(r.meal_total_minutes)} min
                            </span>
                          </div>
                          <div className="divide-y divide-zinc-100">
                            {(r.meal_breaks || []).map((m, i) => (
                              <div key={i} className="px-3 py-2 flex items-center justify-between">
                                <div>
                                  <p className="text-xs font-medium text-zinc-900">{m.label}</p>
                                  <p className="text-[11px] text-zinc-500 font-mono">
                                    {formatTime(m.out)} → {formatTime(m.in)}
                                  </p>
                                </div>
                                <span className="text-xs font-mono text-zinc-700">
                                  {Math.round(m.duration_minutes)} min
                                </span>
                              </div>
                            ))}
                            {r.unclosed_meal_out && (
                              <div className="px-3 py-2 bg-amber-50">
                                <p className="text-[11px] text-amber-700 font-medium">
                                  Salida sin regreso: {formatTime(r.unclosed_meal_out)}
                                </p>
                              </div>
                            )}
                            {typeof r.meal_allowance_minutes === "number" && (
                              <div
                                className="px-3 py-2 bg-zinc-50 flex items-center justify-between"
                                data-testid={`meal-excess-${r.id}`}
                              >
                                <p className="text-[11px] text-zinc-600">
                                  Tope otorgado{" "}
                                  <span className="font-mono">{r.meal_allowance_minutes} min</span>
                                </p>
                                {r.meal_excess_minutes > 0 ? (
                                  <span className="text-[11px] font-semibold text-amber-600 font-mono">
                                    +{Math.round(r.meal_excess_minutes)} min sobre el tope (no rebajado)
                                  </span>
                                ) : (
                                  <span className="text-[11px] font-medium text-emerald-600">
                                    Dentro del tope
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <span className="text-zinc-300 font-mono text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-600 font-mono">
                    {formatTime(r.final_check_out)}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-600 font-mono text-right hidden md:table-cell">
                    {r.ordinary_hours > 0 ? `${r.ordinary_hours.toFixed(2)}h` : "\u2014"}
                  </TableCell>
                  <TableCell className="text-sm font-mono text-right hidden md:table-cell">
                    {r.overtime_hours > 0 ? (
                      <span className="text-violet-700 font-semibold">
                        {r.overtime_hours.toFixed(2)}h
                      </span>
                    ) : (
                      <span className="text-zinc-300">{"\u2014"}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-900 font-mono font-semibold text-right">
                    {r.worked_hours > 0 ? `${r.worked_hours.toFixed(2)}h` : "\u2014"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Badge
                        variant="outline"
                        className={`font-medium ${statusMap[r.attendance_status]?.cls || ""}`}
                      >
                        {statusMap[r.attendance_status]?.label || r.attendance_status}
                      </Badge>
                      {r.manual_adjustment_flag && (
                        <span
                          className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200"
                          title="Registro ajustado manualmente"
                        >
                          AJUSTE
                        </span>
                      )}
                      {r.full_day_credited && (
                        <span
                          className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                          title={`Día completo por comida no tomada (+${Math.round(r.meal_credit_minutes || 0)} min). Motivo: ${r.meal_credit_reason || ""}`}
                          data-testid={`meal-credit-badge-${r.id}`}
                        >
                          DÍA COMPLETO
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {r.manual_adjustment_flag && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-violet-600 hover:text-violet-900 hover:bg-violet-50"
                          onClick={() => openAuditDialog(r)}
                          data-testid={`history-record-${r.id}`}
                          aria-label="Historial de ajustes"
                          title="Historial de ajustes"
                        >
                          <History className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {r.final_check_out && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-900 hover:bg-emerald-50"
                            onClick={() => openReentryDialog(r)}
                            data-testid={`allow-reentry-${r.id}`}
                            aria-label="Permitir reingreso"
                            title="Permitir reingreso (el trabajador regresa al kiosko)"
                          >
                            <LogIn className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-amber-600 hover:text-amber-900 hover:bg-amber-50"
                            onClick={() => openReopenDialog(r)}
                            data-testid={`reopen-record-${r.id}`}
                            aria-label="Anular salida"
                            title="Anular salida (corregir marca incorrecta)"
                          >
                            <Unlock className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                      {r.final_check_out && !r.full_day_credited && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-teal-600 hover:text-teal-900 hover:bg-teal-50"
                          onClick={() => openMealCreditDialog(r)}
                          data-testid={`meal-credit-${r.id}`}
                          aria-label="Día completo (comida no tomada)"
                          title="Registrar día laborado completo (no tomó comida y salió antes)"
                        >
                          <CalendarCheck className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {r.full_day_credited && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-teal-600 hover:text-teal-900 hover:bg-teal-50"
                          onClick={() => revertMealCredit(r)}
                          data-testid={`revert-meal-credit-${r.id}`}
                          aria-label="Revertir día completo"
                          title="Revertir crédito por comida no tomada"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
                        onClick={() => openEditDialog(r)}
                        data-testid={`edit-record-${r.id}`}
                        aria-label="Editar registro"
                        title="Editar registro"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteRecord(r)}
                        data-testid={`delete-record-${r.id}`}
                        aria-label="Eliminar registro"
                        title="Eliminar registro y marcas del día (permite volver a marcar entrada)"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {visibleRecords.length === 0 && !loading && (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    className="text-center py-16 text-zinc-400"
                  >
                    <Clock className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">
                      {onlyNoCheckout
                        ? "No hay registros sin marca de salida para esta fecha"
                        : "No hay registros para esta fecha"}
                    </p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Events Table */}
      <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-zinc-100">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-900">
              Eventos de marcacion ({events.length})
            </h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-100 hover:bg-transparent">
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Fecha
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Empleado
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Tipo
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Hora
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden sm:table-cell">
                  Sucursal
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden md:table-cell">
                  Fuente
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((evt) => (
                <TableRow
                  key={evt.id}
                  className="border-zinc-100"
                  data-testid={`attendance-event-${evt.id}`}
                >
                  <TableCell className="text-sm text-zinc-600 font-mono whitespace-nowrap">
                    {formatDate(evt.date || (evt.event_time || "").slice(0, 10))}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-900 font-medium">
                    {getEmployeeName(evt.employee_id)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-medium text-xs">
                      {eventTypeLabels[evt.event_type] || evt.event_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-600 font-mono">
                    {formatTime(evt.event_time)}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden sm:table-cell">
                    {getBranchName(evt.branch_id)}
                    {isCrossBranchEvent(evt) && (
                      <Badge
                        variant="outline"
                        className="ml-2 bg-amber-50 text-amber-700 border-amber-200 text-[10px] font-medium"
                        title="Marcó en una sucursal distinta a la asignada"
                        data-testid={`cross-branch-badge-${evt.id}`}
                      >
                        Otra sucursal
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-400 hidden md:table-cell">
                    {evt.source === "manual_adjustment_hr" ? "Manual (RRHH)" : evt.source}
                  </TableCell>
                </TableRow>
              ))}
              {events.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-10 text-zinc-400 text-sm"
                  >
                    No hay eventos para esta fecha
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Event Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">
              Registrar marca manual
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Empleado *
              </Label>
              <Select
                value={form.employee_id}
                onValueChange={(v) => setForm({ ...form, employee_id: v })}
              >
                <SelectTrigger data-testid="event-employee-select">
                  <SelectValue placeholder="Seleccionar empleado" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.full_name} ({e.employee_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Tipo de marca *
                </Label>
                <Select
                  value={form.event_type}
                  onValueChange={(v) => setForm({ ...form, event_type: v })}
                >
                  <SelectTrigger data-testid="event-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="check_in">Entrada</SelectItem>
                    <SelectItem value="meal_out">Salida comida</SelectItem>
                    <SelectItem value="meal_in">Regreso comida</SelectItem>
                    <SelectItem value="check_out">Salida</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Hora *
                </Label>
                <Input
                  type="time"
                  value={form.event_time}
                  onChange={(e) =>
                    setForm({ ...form, event_time: e.target.value })
                  }
                  data-testid="event-time-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Sucursal *
              </Label>
              <Select
                value={form.branch_id}
                onValueChange={(v) => setForm({ ...form, branch_id: v })}
              >
                <SelectTrigger data-testid="event-branch-select">
                  <SelectValue placeholder="Seleccionar sucursal" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="event-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateEvent}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="event-submit-button"
            >
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Record Dialog */}
      <Dialog open={!!editRecord} onOpenChange={(v) => !v && closeEditDialog()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">
              Editar registro — {editRecord ? getEmployeeName(editRecord.employee_id) : ""}
            </DialogTitle>
          </DialogHeader>
          {editRecord && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium text-zinc-700">Entrada</Label>
                  <Input
                    type="time"
                    value={editForm.first_check_in_time || ""}
                    onChange={(e) => setEditForm({ ...editForm, first_check_in_time: e.target.value })}
                    data-testid="edit-check-in"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-zinc-700">Salida</Label>
                  <Input
                    type="time"
                    value={editForm.final_check_out_time || ""}
                    onChange={(e) => setEditForm({ ...editForm, final_check_out_time: e.target.value })}
                    data-testid="edit-check-out"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
                <input
                  id="recalc-hours"
                  type="checkbox"
                  checked={!!editForm.recalc_hours}
                  onChange={(e) => setEditForm({ ...editForm, recalc_hours: e.target.checked })}
                  className="h-4 w-4 rounded border-zinc-300"
                  data-testid="edit-recalc-hours"
                />
                <label htmlFor="recalc-hours" className="text-xs text-zinc-700 leading-tight flex-1">
                  Recalcular horas automaticamente a partir de Entrada / Salida (descontando comidas registradas).
                </label>
              </div>

              <div className={`grid grid-cols-3 gap-3 ${editForm.recalc_hours ? "opacity-40 pointer-events-none" : ""}`}>
                <div>
                  <Label className="text-xs font-medium text-zinc-700">Hrs Ordinarias</Label>
                  <Input
                    type="number" step="0.25" min="0"
                    value={editForm.ordinary_hours ?? 0}
                    onChange={(e) => setEditForm({ ...editForm, ordinary_hours: e.target.value })}
                    data-testid="edit-ordinary-hours"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-zinc-700">Hrs Extra</Label>
                  <Input
                    type="number" step="0.25" min="0"
                    value={editForm.overtime_hours ?? 0}
                    onChange={(e) => setEditForm({ ...editForm, overtime_hours: e.target.value })}
                    data-testid="edit-overtime-hours"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-zinc-700">Total Hrs</Label>
                  <Input
                    type="number" step="0.25" min="0"
                    value={editForm.worked_hours ?? 0}
                    onChange={(e) => setEditForm({ ...editForm, worked_hours: e.target.value })}
                    data-testid="edit-worked-hours"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium text-zinc-700">Estado</Label>
                  <Select
                    value={editForm.attendance_status}
                    onValueChange={(v) => setEditForm({ ...editForm, attendance_status: v })}
                  >
                    <SelectTrigger data-testid="edit-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="present">Presente</SelectItem>
                      <SelectItem value="absent">Ausente</SelectItem>
                      <SelectItem value="incomplete">Incompleto</SelectItem>
                      <SelectItem value="vacation">Vacaciones</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-zinc-700">Tardanza (min)</Label>
                  <Input
                    type="number" step="1" min="0"
                    value={editForm.lateness_minutes ?? 0}
                    onChange={(e) => setEditForm({ ...editForm, lateness_minutes: e.target.value })}
                    data-testid="edit-lateness"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium text-zinc-700">Notas del ajuste</Label>
                <Input
                  placeholder="Motivo del ajuste (opcional)"
                  value={editForm.notes || ""}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  data-testid="edit-notes"
                />
              </div>

              <p className="text-[11px] text-zinc-500">
                Al guardar, el registro se marcara como <span className="font-semibold text-violet-700">ajuste manual</span>.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeEditDialog} data-testid="edit-cancel">
              Cancelar
            </Button>
            <Button
              onClick={saveEdit}
              disabled={savingEdit}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="edit-save"
            >
              {savingEdit ? "Guardando..." : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Record Dialog (día sin registro) */}
      <Dialog open={!!manualEmp} onOpenChange={(v) => !v && closeManualDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">
              Agregar registro — {manualEmp?.full_name || ""}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Crea un registro de asistencia para un día sin marcas.
            </DialogDescription>
          </DialogHeader>
          {manualEmp && (
            <div className="space-y-4 py-2">
              <p className="text-xs text-zinc-500">
                Fecha: <span className="font-mono font-medium text-zinc-700">{formatDate(date)}</span>
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium text-zinc-700">Entrada</Label>
                  <Input
                    type="time"
                    value={manualForm.first_check_in_time || ""}
                    onChange={(e) => setManualForm({ ...manualForm, first_check_in_time: e.target.value })}
                    data-testid="manual-check-in"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-zinc-700">Salida</Label>
                  <Input
                    type="time"
                    value={manualForm.final_check_out_time || ""}
                    onChange={(e) => setManualForm({ ...manualForm, final_check_out_time: e.target.value })}
                    data-testid="manual-check-out"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">Estado</Label>
                <Select
                  value={manualForm.attendance_status}
                  onValueChange={(v) => setManualForm({ ...manualForm, attendance_status: v })}
                >
                  <SelectTrigger data-testid="manual-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="present">Presente</SelectItem>
                    <SelectItem value="absent">Ausente</SelectItem>
                    <SelectItem value="incomplete">Incompleto</SelectItem>
                    <SelectItem value="vacation">Vacaciones</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">Notas</Label>
                <Input
                  placeholder="Motivo del registro manual (opcional)"
                  value={manualForm.notes || ""}
                  onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
                  data-testid="manual-notes"
                />
              </div>
              <p className="text-[11px] text-zinc-500">
                Las horas se calculan de Entrada/Salida. El registro se marca como{" "}
                <span className="font-semibold text-violet-700">ajuste manual</span> y se conserva al consolidar.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeManualDialog} data-testid="manual-cancel">
              Cancelar
            </Button>
            <Button
              onClick={saveManual}
              disabled={savingManual}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="manual-save"
            >
              {savingManual ? "Guardando..." : "Crear registro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit History Dialog */}
      <Dialog open={!!auditRecord} onOpenChange={(v) => !v && closeAuditDialog()}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-zinc-950 flex items-center gap-2">
              <History className="w-4 h-4 text-violet-600" />
              Historial de ajustes
              {auditRecord && (
                <span className="text-xs font-normal text-zinc-500 font-mono ml-2">
                  {getEmployeeName(auditRecord.employee_id)} · {formatDate(auditRecord.date)}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 max-h-[60vh] overflow-y-auto">
            {auditLoading ? (
              <p className="text-sm text-zinc-500 text-center py-6">Cargando...</p>
            ) : auditHistory.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-6">
                Este registro no tiene ajustes manuales.
              </p>
            ) : (
              <ol className="relative border-l-2 border-violet-200 ml-2 space-y-4">
                {auditHistory.map((h) => {
                  const keys = Object.keys(h.after || {});
                  return (
                    <li key={h.id} className="ml-4" data-testid={`audit-entry-${h.id}`}>
                      <div className="absolute -left-[7px] w-3 h-3 rounded-full bg-violet-500 border-2 border-white" />
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="text-xs font-semibold text-zinc-900">
                          {h.changed_by || "RRHH"}
                        </span>
                        <span className="text-[11px] font-mono text-zinc-500">
                          {formatDateTime(h.changed_at)}
                        </span>
                      </div>
                      {h.notes && (
                        <p className="text-xs text-zinc-600 italic mb-1.5">&ldquo;{h.notes}&rdquo;</p>
                      )}
                      <div className="text-xs space-y-1">
                        {keys.length === 0 ? (
                          <p className="text-zinc-400">Sin cambios registrados.</p>
                        ) : (
                          keys.map((k) => {
                            const before = h.before?.[k];
                            const after = h.after?.[k];
                            const fmt = (v) => {
                              if (v == null || v === "") return "—";
                              if (typeof v === "string" && v.length > 19 && v.includes("T")) {
                                return v.slice(11, 16);
                              }
                              return String(v);
                            };
                            return (
                              <div key={k} className="flex items-start gap-2">
                                <span className="font-mono text-zinc-500 min-w-[110px] shrink-0">
                                  {k}
                                </span>
                                <span className="text-zinc-400 line-through">{fmt(before)}</span>
                                <span className="text-zinc-400">→</span>
                                <span className="text-violet-700 font-semibold">{fmt(after)}</span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeAuditDialog}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Anular salida (corrige marca incorrecta) */}
      <Dialog open={!!reopenRecord} onOpenChange={(v) => !v && closeReopenDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950 flex items-center gap-2">
              <Unlock className="w-4 h-4 text-amber-600" /> Anular salida
            </DialogTitle>
          </DialogHeader>
          {reopenRecord && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg border border-amber-100 bg-amber-50/40 px-4 py-3 text-sm">
                <p className="text-zinc-900 font-medium leading-tight">
                  {getEmployeeName(reopenRecord.employee_id)}
                </p>
                <p className="text-xs text-zinc-500 font-mono mt-0.5">
                  {getEmployeeCode(reopenRecord.employee_id)} · {formatDate(reopenRecord.date)}
                </p>
                <p className="text-xs text-zinc-600 mt-2 leading-relaxed">
                  <strong>Use esta opción cuando la salida fue marcada por error.</strong>
                  La salida se descarta del registro como si nunca hubiera ocurrido. El kiosko
                  permitirá marcar nuevamente. Queda historial del ajuste.
                </p>
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Motivo (opcional)
                </Label>
                <Input
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value)}
                  placeholder="Ej: salida marcada por equivocación"
                  data-testid="reopen-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeReopenDialog} data-testid="reopen-cancel">
              Cancelar
            </Button>
            <Button
              onClick={confirmReopen}
              disabled={reopening}
              className="bg-amber-600 text-white hover:bg-amber-700"
              data-testid="reopen-confirm"
            >
              <Unlock className="w-4 h-4 mr-2" />
              {reopening ? "Anulando…" : "Anular salida"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación: resolver todas las alertas (cuando hay muchas) */}
      <Dialog open={confirmResolveAll} onOpenChange={setConfirmResolveAll}>
        <DialogContent className="sm:max-w-md" data-testid="resolve-all-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-950 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" /> Resolver todas las alertas
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-zinc-600">
              Vas a marcar como resueltas{" "}
              <span className="font-semibold text-zinc-900" data-testid="resolve-all-count">
                {alerts.length} alertas
              </span>
              {(alertFilterBranch !== "all" || alertFilterDate !== "all" || alertFilterEmployee !== "all")
                ? " (según los filtros activos)"
                : ""}
              . Esta acción no se puede deshacer. ¿Deseas continuar?
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmResolveAll(false)}
              data-testid="resolve-all-cancel"
            >
              Cancelar
            </Button>
            <Button
              className="bg-amber-600 text-white hover:bg-amber-700"
              onClick={resolveAllAlerts}
              data-testid="resolve-all-confirm"
            >
              Sí, resolver {alerts.length}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Día completo por comida no tomada (RRHH/Admin) */}
      <Dialog open={!!mealCreditRecord} onOpenChange={(v) => !v && closeMealCreditDialog()}>
        <DialogContent className="sm:max-w-md" data-testid="meal-credit-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-950 flex items-center gap-2">
              <CalendarCheck className="w-4 h-4 text-teal-600" /> Día laborado completo
            </DialogTitle>
          </DialogHeader>
          {mealCreditRecord && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg border border-teal-100 bg-teal-50/40 px-4 py-3 text-sm">
                <p className="text-zinc-900 font-medium leading-tight">
                  {getEmployeeName(mealCreditRecord.employee_id)}
                </p>
                <p className="text-xs text-zinc-500 font-mono mt-0.5">
                  {getEmployeeCode(mealCreditRecord.employee_id)} · {formatDate(mealCreditRecord.date)}
                </p>
                <p className="text-xs text-zinc-600 mt-2 leading-relaxed">
                  Acredita el tiempo de comida <strong>no tomado</strong> (tope{" "}
                  {Math.round(mealCreditRecord.meal_allowance_minutes ?? 60)} min, tomado{" "}
                  {Math.round(mealCreditRecord.meal_total_minutes ?? 0)} min) para cubrir la
                  salida anticipada, sin superar la jornada ordinaria. No genera horas extra.
                </p>
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">Motivo *</Label>
                <Textarea
                  value={mealCreditReason}
                  onChange={(e) => setMealCreditReason(e.target.value)}
                  rows={3}
                  placeholder="Ej. No tomó los tiempos de comida y salió antes"
                  data-testid="meal-credit-reason"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeMealCreditDialog} data-testid="meal-credit-cancel">
              Cancelar
            </Button>
            <Button
              onClick={confirmMealCredit}
              disabled={mealCrediting}
              className="bg-teal-600 text-white hover:bg-teal-700"
              data-testid="meal-credit-confirm"
            >
              {mealCrediting ? "Aplicando…" : "Registrar día completo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permitir reingreso (split shift) */}
      <Dialog open={!!reentryRecord} onOpenChange={(v) => !v && closeReentryDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950 flex items-center gap-2">
              <LogIn className="w-4 h-4 text-emerald-600" /> Permitir reingreso
            </DialogTitle>
          </DialogHeader>
          {reentryRecord && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 px-4 py-3 text-sm">
                <p className="text-zinc-900 font-medium leading-tight">
                  {getEmployeeName(reentryRecord.employee_id)}
                </p>
                <p className="text-xs text-zinc-500 font-mono mt-0.5">
                  {getEmployeeCode(reentryRecord.employee_id)} · {formatDate(reentryRecord.date)}
                </p>
                <p className="text-xs text-zinc-600 mt-2 leading-relaxed">
                  <strong>Use esta opción cuando el trabajador salió por diligencia/permiso y regresa a laborar.</strong>
                  La salida se mantiene como REAL; se habilita una nueva entrada en el kiosko.
                  El tiempo entre la salida y la nueva entrada NO se paga. El permiso caduca a las 23:59
                  o cuando el trabajador marque entrada.
                </p>
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Motivo (opcional)
                </Label>
                <Input
                  value={reentryReason}
                  onChange={(e) => setReentryReason(e.target.value)}
                  placeholder="Ej: cita médica · diligencia bancaria · permiso personal"
                  data-testid="reentry-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeReentryDialog} data-testid="reentry-cancel">
              Cancelar
            </Button>
            <Button
              onClick={confirmReentry}
              disabled={allowingReentry}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="reentry-confirm"
            >
              <LogIn className="w-4 h-4 mr-2" />
              {allowingReentry ? "Habilitando…" : "Permitir reingreso"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteRecord} onOpenChange={(v) => !v && setDeleteRecord(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950 flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-red-600" /> Eliminar registro
            </DialogTitle>
          </DialogHeader>
          {deleteRecord && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg border border-red-100 bg-red-50/50 px-4 py-3 text-sm">
                <p className="text-zinc-900 font-medium leading-tight">
                  {getEmployeeName(deleteRecord.employee_id)}
                </p>
                <p className="text-xs text-zinc-500 font-mono mt-0.5">
                  {getEmployeeCode(deleteRecord.employee_id)} · {formatDate(deleteRecord.date)}
                </p>
                <p className="text-xs text-zinc-600 mt-2 leading-relaxed">
                  Se eliminará el registro consolidado <strong>y todas las marcas (entrada, comidas, salida)
                  de ese trabajador ese día</strong>. Quedará como si no hubiera marcado y podrá volver a
                  marcar entrada en el kiosko. Esta acción no se puede deshacer.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteRecord(null)} data-testid="delete-cancel">
              Cancelar
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={deletingRecord}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="delete-confirm"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {deletingRecord ? "Eliminando…" : "Eliminar registro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
